<template>
  <a-drawer
    :visible="visible"
    title="管理原料组合包"
    width="800px"
    :maskClosable="false"
    :closable="true"
    @close="handleCancel"
    :bodyStyle="{ padding: '24px', paddingBottom: '60px' }"
  >
    <a-spin :spinning="loading" tip="加载中...">
      <div class="manage-raw-bag-container">
        <!-- 信息提示 -->
        <a-alert
          type="info"
          show-icon
          :message="alertMessage"
          style="margin-bottom: 24px"
        />

        <!-- 组合包列表 -->
        <div v-for="(bag, bagIndex) in rawBagList" :key="bag.id || bagIndex" class="bag-section">
          <div class="bag-header">
            <h3 class="bag-title">{{ bag.rawBagName || bag.bagName }}</h3>
            <a-button type="link" danger size="small" @click="handleCancelBag(bag)">
              取消组合包
            </a-button>
          </div>
          
          <a-table
            :columns="columns"
            :dataSource="bag.detailList || bag.formulaRawBagDetailVOList || []"
            :pagination="false"
            :rowKey="(record, index) => record.id || record.rawId || index"
            size="small"
            bordered
            :loading="false"
            class="raw-bag-table"
          >
            <template slot="checkbox" slot-scope="text, record, index">
              <a-radio 
                :checked="record.isMajor == 1"
                @change="(e) => handleRadioChange(e, bagIndex, index)"
              />
            </template>
            <template slot="rawName" slot-scope="text, record">
              {{ record.rawName || record.name || '' }}
            </template>
            <template slot="code" slot-scope="text, record">
              {{ record.code || record.rawCode || '' }}
            </template>
            <template slot="ratio" slot-scope="text, record, index">
              <a-input-number
                :value="record.percentage || record.ratio || 0"
                :min="0"
                :max="100"
                :precision="2"
                :step="0.1"
                size="small"
                style="width: 100%"
                @change="(value) => handleRatioChange(value, bagIndex, index)"
              />
            </template>
            <template slot="action" slot-scope="text, record, index">
              <a-button 
                type="link" 
                danger 
                size="small" 
                :disabled="record.isMajor == 1"
                @click="handleDeleteRaw(bagIndex, index)"
              >
                删除
              </a-button>
            </template>
          </a-table>
        </div>

        <!-- 空状态 -->
        <a-empty v-if="!loading && rawBagList.length === 0" description="暂无原料组合包" />
      </div>
    </a-spin>

    <footer class="drawer-footer">
      <a-button style="margin-right: 8px" @click="handleCancel">取消</a-button>
      <a-button type="primary" :loading="saving" @click="handleSave">保存</a-button>
    </footer>
  </a-drawer>
</template>

<script>
import { post } from '@/Utils/axios'

export default {
  name: 'ManageRawBag',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    formulaId: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      saving: false,
      rawBagList: [],
      alertMessage: '原料组合包:在配合饲料中维护原料约束时,将根据组合包中主副原料用量比例,进行副原料用量约束更新;组合包中原料数量≤3个。',
      columns: [
        {
          title: '主原料',
          key: 'checkbox',
          width: 80,
          align: 'center',
          scopedSlots: { customRender: 'checkbox' }
        },
        {
          title: '原料',
          dataIndex: 'rawName',
          key: 'rawName',
          width: 200,
          scopedSlots: { customRender: 'rawName' }
        },
        {
          title: '代码',
          dataIndex: 'code',
          key: 'code',
          width: 150,
          scopedSlots: { customRender: 'code' }
        },
        {
          title: '比例(%)',
          dataIndex: 'ratio',
          key: 'ratio',
          width: 150,
          align: 'center',
          scopedSlots: { customRender: 'ratio' }
        },
        {
          title: '操作',
          key: 'action',
          width: 100,
          align: 'center',
          scopedSlots: { customRender: 'action' }
        }
      ]
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.loadRawBagList()
      }
    }
  },
  methods: {
    async loadRawBagList() {
      this.loading = true
      try {
        // 查询原料组合包列表
        const res = await post('/nhf-app/formularawbag/listAllFormulaRawBagVO', {
          formulaId: this.formulaId
        })
        
        if (res && res.success && res.data && Array.isArray(res.data)) {
          // 初始化每个组合包的详情列表，并添加checked属性，规范化字段名
          this.rawBagList = res.data.map(bag => {
            const detailList = (bag.formulaRawBagDetailVOList || []).map(item => {
              // 规范化字段名，确保显示正确
              return {
                id: item.id,
                rawId: item.rawId,
                rawName: item.rawName || '',
                code: item.rawCode || '',
                percentage: item.percentage || 0,
                ratio: item.percentage || 0,
                isMajor: item.isMajor || 0,
                checked: false // 默认不选中
              }
            })
            return {
              ...bag,
              detailList
            }
          })
        } else {
          this.rawBagList = []
        }
      } catch (error) {
        console.error('加载原料组合包列表失败:', error)
        this.$message.error('加载原料组合包列表失败')
        this.rawBagList = []
      } finally {
        this.loading = false
      }
    },

    handleCheckboxChange(e, bagIndex, detailIndex) {
      if (this.rawBagList[bagIndex] && this.rawBagList[bagIndex].detailList) {
        this.$set(this.rawBagList[bagIndex].detailList[detailIndex], 'checked', e.target.checked)
      }
    },

    handleRadioChange(e, bagIndex, detailIndex) {
      if (this.rawBagList[bagIndex]) {
        const detailList = this.rawBagList[bagIndex].detailList || this.rawBagList[bagIndex].formulaRawBagDetailVOList
        if (detailList) {
          // 将所有原料的 isMajor 设为 0
          detailList.forEach((item, index) => {
            this.$set(item, 'isMajor', index === detailIndex ? 1 : 0)
          })
        }
      }
    },

    handleRatioChange(value, bagIndex, detailIndex) {
      if (this.rawBagList[bagIndex] && this.rawBagList[bagIndex].detailList) {
        const detail = this.rawBagList[bagIndex].detailList[detailIndex]
        // 同步更新 percentage 和 ratio
        this.$set(detail, 'percentage', value || 0)
        this.$set(detail, 'ratio', value || 0)
      }
    },

    handleDeleteRaw(bagIndex, detailIndex) {
      const bag = this.rawBagList[bagIndex]
      const detailList = bag.detailList || bag.formulaRawBagDetailVOList || []
      
      // 检查是否是主原料
      const currentRaw = detailList[detailIndex]
      if (currentRaw && currentRaw.isMajor == 1) {
        this.$message.warning('主原料不能删除，请先切换主原料')
        return
      }
      
      if (detailList.length <= 2) {
        this.$message.warning('至少需要2个原料')
        return
      }

      // 前端移除数据
      if (bag.detailList) {
        bag.detailList.splice(detailIndex, 1)
      } else if (bag.formulaRawBagDetailVOList) {
        bag.formulaRawBagDetailVOList.splice(detailIndex, 1)
      }
      this.$message.success('操作后，需点击【保存】才能生效。')
    },

    handleCancelBag(bag) {
      // 前端移除数据
      const bagIndex = this.rawBagList.findIndex(b => b.id === bag.id || b === bag)
      if (bagIndex !== -1) {
        this.rawBagList.splice(bagIndex, 1)
        this.$message.success('操作后，需点击【保存】才能生效。')
      }
    },

    formatRatio(value) {
      const num = Number(value) || 0
      return num.toFixed(0)
    },

    async handleSave() {
      // 验证每个组合包的比例总和是否为100
      for (let i = 0; i < this.rawBagList.length; i++) {
        const bag = this.rawBagList[i]
        const detailList = bag.detailList || bag.formulaRawBagDetailVOList || []
        
        if (detailList.length === 0) {
          this.$message.warning(`组合包"${bag.rawBagName || bag.bagName}"中至少需要1个原料`)
          return
        }
        
        // 计算比例总和
        const totalPercentage = detailList.reduce((sum, detail) => {
          const percentage = Number(detail.percentage || detail.ratio || 0)
          return sum + percentage
        }, 0)
        
        // 允许0.01的误差范围，避免浮点数精度问题
        if (Math.abs(totalPercentage - 100) > 0.01) {
          this.$message.warning(
            `组合包"${bag.rawBagName || bag.bagName}"的比例总和为 ${totalPercentage.toFixed(2)}%，必须等于100%`
          )
          return
        }
      }

      this.saving = true
      try {
        // 构建请求数据
        const formulaRawBagVOList = this.rawBagList.map(bag => {
          const detailList = bag.detailList || bag.formulaRawBagDetailVOList || []
          
          return {
            createTime: bag.createTime || '',
            createUser: bag.createUser || 0,
            formulaId: this.formulaId,
            formulaRawBagDetailVOList: detailList.map(detail => ({
              percentage: detail.percentage || detail.ratio || 0,
              rawId: detail.rawId,
              isMajor: detail.isMajor || 0
            })),
            id: bag.id || 0,
            isDeleted: bag.isDeleted || 0,
            rawBagName: bag.rawBagName || bag.bagName || '',
            status: bag.status || 0,
            updateTime: bag.updateTime || '',
            updateUser: bag.updateUser || 0
          }
        })

        const requestData = {
          formulaId: this.formulaId,
          formulaRawBagVOList
        }

        // 调用更新接口
        const res = await post('/nhf-app/formularawbag/updateAllFormulaRawBag', requestData)
        
        if (res && res.success !== false) {
          this.$message.success('保存成功')
          // 重新加载列表以获取最新数据
          await this.loadRawBagList()
          // 通知父组件刷新原料列表
          this.$emit('refresh')
          this.handleCancel()
        } else {
          this.$message.error(res?.msg || '保存失败，请重试')
        }
      } catch (error) {
        console.error('保存失败:', error)
        this.$message.error(error?.msg || '保存失败，请重试')
      } finally {
        this.saving = false
      }
    },

    handleCancel() {
      this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
.manage-raw-bag-container {
  .bag-section {
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    padding: 16px;
    margin-bottom: 24px;
    
    .bag-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      
      .bag-title {
        font-size: 16px;
        font-weight: bold;
        color: #333;
        margin: 0;
      }
    }
  }
  
  .ratio-cell {
    background-color: #e6f7ff;
    padding: 4px 8px;
    border-radius: 4px;
    display: inline-block;
    min-width: 50px;
    text-align: center;
  }
}

.drawer-footer {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 56px;
  border-top: 1px solid #e8e8e8;
  padding: 10px 16px;
  background: #fff;
  text-align: right;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  
  .ant-btn {
    margin-right: 8px;
    min-width: 60px;
  }
}

::v-deep .ant-table-thead > tr > th {
  background-color: #fafafa;
  font-weight: 600;
}

::v-deep .ant-table-tbody > tr > td {
  border-bottom: 1px solid #f0f0f0;
}

::v-deep .ant-drawer-body {
  padding-bottom: 0;
}

::v-deep .ant-drawer-footer {
  padding: 10px 16px;
  border-top: 1px solid #e8e8e8;
}
</style>

